import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Edit3, Plus, Star, Package } from "lucide-react";
import ProductFormModal from "@/components/product/ProductFormModal";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";
const API = `${BACKEND_URL}`;

export default function ProductsManagement() {
  const [showProductModal, setShowProductModal] = React.useState(false);
  const [editingProduct, setEditingProduct] = React.useState(null);
  const [categories, setCategories] = React.useState([]);

  const [products, setProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [fetching, setFetching] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    image_url: "",
    quantity: "",
    min_stock: "",
    ingredients: [],
    available: true, // Novo produto sempre começa como true
  });

  // Busca e filtro
  const [searchQuery, setSearchQuery] = useState("");
  // 'all' representa sem filtro — evita usar string vazia em SelectItem
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchIngredients();
  }, []);

  const fetchProducts = async () => {
    setFetching(true);
    try {
      const response = await axios.get(`${API}/products/admin/all`);
      // Normaliza diferentes formatos de resposta: { data: [...] } ou [...]
      const payload = response.data?.data ?? response.data ?? [];
      setProducts(Array.isArray(payload) ? payload : []);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setFetching(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchIngredients = async () => {
    try {
      const response = await axios.get(`${API}/ingredients`);
      setIngredients(response.data.data);
    } catch (error) {
      console.error("Error fetching ingredients:", error);
    }
  };

  const handleSaveProduct = async (payload, editing) => {
    //console.log(JSON.stringify(payload));
    try {
      if (editing) {
        await axios.put(`${API}/products/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/products`, payload);
      }
      await fetchProducts();
      setShowProductModal(false);
    } catch (error) {
      console.error("Error saving product:", error);
    }
  };

  const handleEdit = (product) => {
    //console.log("Editing product:", JSON.stringify(product));
    setEditingProduct(product);
    const selectedIds = Array.isArray(product?.ingredients)
      ? product.ingredients.map((i) => String(i.id))
      : [];

    setFormData({
      name: product?.name ?? "",
      description: product?.description ?? "",
      price: Number(product?.price ?? 0),
      category: product?.category ?? "",
      image_url: product?.image_url ?? "",
      quantity: Number(product?.quantity ?? 0),
      min_stock: Number(product?.min_stock ?? 0),
      available:
        typeof product?.available === "boolean" ? product.available : true,

      creatingFlag: (product?.creating ?? "N") === "S",
      unid: product?.unid ?? "",
      weight: Number(product?.weight ?? 0),
      unid_product: product?.unid_product ?? "",
      weight_product: Number(product?.weight_product ?? 0),
      totalIngredients: Number(product?.totalIngredients ?? 0),
    });

    setShowProductModal(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm("Tem certeza que deseja excluir este produto?")) {
      try {
        await axios.delete(`${API}/products/${productId}`);
        await fetchProducts();
      } catch (error) {
        console.error("Error deleting product:", error);
      }
    }
  };

  // Normalização e filtragem dos produtos (busca por nome e filtro por categoria)
  const normalize = (s) => (s ?? "").toString().toLowerCase();
  const filteredProducts = Array.isArray(products)
    ? products.filter((p) => {
        const matchesName = normalize(p.name).includes(normalize(searchQuery));
        const matchesCategory =
          selectedCategory && selectedCategory !== "all"
            ? String(p.category) === String(selectedCategory)
            : true;
        return matchesName && matchesCategory;
      })
    : [];

  return (
    <div className="space-y-6">
      {/* Modal de Loading */}
      <Dialog open={fetching} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-sm"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-gray-200 border-t-[#c9332C] rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Package className="h-6 w-6 text-[#c9332C]" />
              </div>
            </div>
            <p className="mt-6 text-base font-semibold text-gray-900 text-center">
              Carregando produtos...
            </p>
            <p className="mt-2 text-sm text-gray-500 text-center">
              Por favor, aguarde
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="h-6 w-6 sm:h-8 sm:w-8" />
            Gestão de Produtos
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {filteredProducts.length} produto
            {filteredProducts.length !== 1 ? "s" : ""} encontrado
            {filteredProducts.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-3">
            {/* Campo de Busca */}
            <Input
              placeholder="Buscar por nome do produto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />

            {/* Filtros e Botão */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Select
                onValueChange={(val) => setSelectedCategory(val)}
                value={selectedCategory}
              >
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Todas as categorias" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <button
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 text-primary-foreground shadow h-9 px-4 py-2 w-full sm:flex-1"
                style={{ background: "#c9332C" }}
                onMouseEnter={(e) => (e.target.style.background = "#a02820")}
                onMouseLeave={(e) => (e.target.style.background = "#c9332C")}
                onClick={() => {
                  setEditingProduct(null);
                  setShowProductModal(true);
                }}
              >
                <Plus className="h-4 w-4" />
                Novo Produto
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Produto */}
      <ProductFormModal
        open={showProductModal}
        onOpenChange={setShowProductModal}
        editingProduct={editingProduct}
        onSave={handleSaveProduct}
        categories={categories}
        ingredientsCatalog={ingredients}
        productsCatalog={products}
      />

      {/* Grid de Produtos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {!fetching && filteredProducts.length === 0 ? (
          <div className="col-span-full text-center text-gray-500 py-10">
            <Package className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">
              {searchQuery || selectedCategory !== "all"
                ? "Nenhum produto encontrado"
                : "Nenhum produto cadastrado"}
            </p>
            <p className="text-sm text-gray-400 mt-2">
              {searchQuery || selectedCategory !== "all"
                ? "Tente ajustar os filtros de busca"
                : "Clique em 'Novo Produto' para começar"}
            </p>
          </div>
        ) : (
          filteredProducts.map((product) => {
            const category = categories.find(
              (cat) => cat.id === product.category
            );
            const isUnavailable = product?.available === false;
            const quantity = Number(product?.quantity || 0);
            const minStock = Number(product?.min_stock || 0);
            const isLowStock = minStock > 0 && quantity <= minStock;

            return (
              <Card
                key={product.id}
                className={`overflow-hidden hover:shadow-lg transition-shadow ${
                  isUnavailable ? "bg-gray-100 border-gray-300" : ""
                } ${
                  isLowStock && !isUnavailable
                    ? "border-2 border-orange-500 shadow-orange-200"
                    : ""
                }`}
              >
                {/* Imagem do Produto - Compacta */}
                <div className="aspect-[4/3] relative">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className={`w-full h-full object-cover ${
                      isUnavailable ? "grayscale opacity-70" : ""
                    }`}
                  />
                  {isLowStock && !isUnavailable && (
                    <Badge className="absolute top-2 left-2 bg-orange-500 text-white text-xs animate-pulse">
                      Estoque Baixo
                    </Badge>
                  )}
                  {isUnavailable && (
                    <Badge className="absolute top-2 left-2 bg-gray-600 text-white text-xs">
                      Indisponível
                    </Badge>
                  )}
                </div>

                {/* Conteúdo - Compacto */}
                <CardContent className="p-3">
                  {/* Título e Preço */}
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <h3 className="font-semibold text-sm leading-tight line-clamp-2 flex-1">
                      {product.name}
                    </h3>
                    <span
                      className={`text-base font-bold whitespace-nowrap ${
                        isUnavailable ? "text-gray-500" : "text-green-600"
                      }`}
                    >
                      R$ {product.price.toFixed(2)}
                    </span>
                  </div>

                  {/* Descrição - Reduzida */}
                  <p
                    className={`text-xs mb-2 line-clamp-2 ${
                      isUnavailable ? "text-gray-500" : "text-gray-600"
                    }`}
                  >
                    {product.description}
                  </p>

                  {/* Categoria */}
                  <div className="mb-2">
                    <Badge variant="outline" className="text-xs">
                      {category?.name}
                    </Badge>
                  </div>

                  {/* Informações de Estoque - Compacto */}
                  <div
                    className={`mb-3 p-2 rounded text-xs ${
                      isLowStock
                        ? "bg-orange-50 border border-orange-200"
                        : "bg-blue-50 border border-blue-200"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={
                          isLowStock
                            ? "text-orange-700 font-medium"
                            : "text-blue-700 font-medium"
                        }
                      >
                        Estoque:
                      </span>
                      <span
                        className={
                          isLowStock
                            ? "text-orange-900 font-bold"
                            : "text-blue-900 font-bold"
                        }
                      >
                        {quantity} un.
                      </span>
                    </div>
                    {minStock > 0 && (
                      <div className="flex items-center justify-between text-[10px] mt-0.5 text-gray-600">
                        <span>Mínimo:</span>
                        <span>{minStock}</span>
                      </div>
                    )}
                  </div>

                  {/* Botões - Compactos */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(product)}
                      className="flex-1 h-8 text-xs"
                    >
                      <Edit3 className="h-3 w-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(product.id)}
                      className="flex-1 h-8 text-xs text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Excluir
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
